import { isNull, set } from 'lodash/fp';
import { toast } from 'react-toastify';
import { all, call, debounce, delay, fork, put, select, takeLatest, takeLeading } from 'redux-saga/effects';

import { askPlaygroundQuestions, confirmCorrectPlaygroundQuestions, fetchAllPlaygroundQuestionsAndAnswers } from '../../../../../services/ai';
import { fetchMLTestDocuments, getMLTestDocumentById, openMLTestDocument, uploadMLTestDocuments } from '../../../../../services/mlTest';
import { getDocumentUrl } from '../../../../../utils/getDocumentUrl';
import { MLData } from '../../../../datasets/store/mlTypes';
import { setIsCheckingHighlightIndexes } from '../../../../documents/document-viewer/store/actions';
import { getPdfText } from '../../../../documents/document-viewer/store/selectors';
import { PdfPageText } from '../../../../documents/document-viewer/store/types';
import { getHighlightIndexesForPageText } from '../../../../documents/document-viewer/store/utils';
import { ColumnSort, TableFilters } from '../../../../shared/table/types';
import { askAIPlaygroundQuestionsFailed, askAIPlaygroundQuestionsSuccessful, fetchAllPlaygroundQuestionsAndAnswersFailed, fetchAllPlaygroundQuestionsAndAnswersStarted, fetchAllPlaygroundQuestionsAndAnswersSuccessful, fetchMLTestDocumentsFailed, fetchMLTestDocumentsStarted, fetchMLTestDocumentsSuccessful, openMLTestDocumentFailed, openMLTestDocumentStarted, openMLTestDocumentSuccessful, setInitialUploadMLTestDocuments, setIsFilteringSelectedQueries, setPlaygroundAIAnswerHighlightIndexes, updateCorrectPlaygroundQueries, uploadMLTestDocumentFailed, uploadMLTestDocumentSuccessful } from './actions';
import { getAIResults, getCurrentCorrectQueries, getCurrentSelectedQueries, getFileDocumentNameIds, getMLDocumentsTableColumnSort, getMLDocumentsTableFilters, getMLDocumentsTablePageNumber, getMLDocumentsTablePageSize, getMLTestDocument, getMlTestDocumentExtractId, getMLTestFilesToUpload } from './selectors';
import { AdminAIPlaygroundActionTypes, AIPlaygroundRequest, AllPreviousPlaygroundQuestionsAndAnswers, FileWithDocumentNameId, MLTestDocument, MLTestDocumentTable, PlaygroundQuestion } from './types';

export function* attemptFetchMLTestDocuments({ payload }: ReturnType<typeof fetchMLTestDocumentsStarted>) {
    try {
        const { pageNumber } = payload;
        const filters: TableFilters = yield select(getMLDocumentsTableFilters);
        const columnSort: ColumnSort | undefined = yield select(getMLDocumentsTableColumnSort);
        const pageSize: number = yield select(getMLDocumentsTablePageSize);
        const { documents, total }: { documents: MLTestDocumentTable[]; total: number; } = yield call(fetchMLTestDocuments, { pageNumber, pageSize, filters, columnSort });
        yield put(fetchMLTestDocumentsSuccessful(documents, total, pageNumber));
    } catch (e) {
        yield put(fetchMLTestDocumentsFailed((e as Error).message));
    }
}

function* attemptFetchMLTestDocumentsWatcher() {
    yield takeLatest(AdminAIPlaygroundActionTypes.FETCH_ML_TEST_DOCUMENTS_STARTED, attemptFetchMLTestDocuments);
}

// ML Test Documents Pagination Sagas

export function* mlTestDocumentsPaginationNextSaga() {
    const pageNumber: number = yield select(getMLDocumentsTablePageNumber);
    yield put(fetchMLTestDocumentsStarted(pageNumber + 1));
}

function* mlTestDocumentsPaginationNextWatcher() {
    yield takeLatest(AdminAIPlaygroundActionTypes.ML_TEST_DOCUMENTS_PAGINATION_NEXT, mlTestDocumentsPaginationNextSaga);
}

export function* mlTestDocumentsPaginationPreviousSaga() {
    const pageNumber: number = yield select(getMLDocumentsTablePageNumber);
    yield put(fetchMLTestDocumentsStarted(pageNumber - 1));
}

function* mlTestDocumentsPaginationPreviousWatcher() {
    yield takeLatest(AdminAIPlaygroundActionTypes.ML_TEST_DOCUMENTS_PAGINATION_PREVIOUS, mlTestDocumentsPaginationPreviousSaga);
}

export function* mlTestDocumentsTableConfigUpdated() {
    yield put(fetchMLTestDocumentsStarted(1));
}

function* mlTestDocumentsTableConfigUpdatedWatcher() {
    yield debounce(200, [AdminAIPlaygroundActionTypes.SET_ML_TEST_DOCUMENTS_TABLE_FILTERS, AdminAIPlaygroundActionTypes.CLEAR_ML_TEST_DOCUMENTS_TABLE_FILTERS, AdminAIPlaygroundActionTypes.SET_ML_TEST_DOCUMENTS_TABLE_COLUMN_SORT, AdminAIPlaygroundActionTypes.SET_ML_TEST_DOCUMENTS_PAGE_SIZE], mlTestDocumentsTableConfigUpdated);
}

export function* uploadFiles(files: File[], details: FileWithDocumentNameId[]) {
    let request = new FormData();
    const numberOfFilesToUpload = files.reduce((acc, cur) => {
        const updatedTotalFileSize = acc.total + cur.size;
        if (updatedTotalFileSize < MAX_UPLOAD_SIZE) {
            return { total: updatedTotalFileSize, numberOfFiles: acc.numberOfFiles + 1 };
        }
        return acc;
    }, { total: 0, numberOfFiles: 0 }).numberOfFiles;
    const filesToUpload = files.slice(0, numberOfFilesToUpload);
    filesToUpload.forEach(file => request.append('files', file));
    const fileInfo = filesToUpload.map(file => details.find(({ documentDescription }) => documentDescription === file.name));
    request.append('fileInfo', JSON.stringify(fileInfo));
    const uploadedDocuments: MLTestDocument[] = yield call(uploadMLTestDocuments, request);
    files.splice(0, numberOfFilesToUpload);
    yield put(setInitialUploadMLTestDocuments(files));
    return uploadedDocuments;
}

// 80MB
const MAX_UPLOAD_SIZE = 8e7;

export function* attemptUploadMLTestDocuments() {
    try {
        const allFiles: File[] = yield select(getMLTestFilesToUpload);
        const details: FileWithDocumentNameId[] = yield select(getFileDocumentNameIds);
        let files = [...allFiles];
        const someFilesTooBig = files.some(file => file.size > MAX_UPLOAD_SIZE);
        if (someFilesTooBig) {
            toast.error('Some of the files you have attempted to upload are too large (over 80mb), please compress them and try again.');
            return;
        }
        while (files.length) {
            yield call(uploadFiles, files, details);
            const updatedAllFiles: File[] = yield select(getMLTestFilesToUpload);
            files = [...updatedAllFiles];
        }
        yield put(uploadMLTestDocumentSuccessful());
        yield put(fetchMLTestDocumentsStarted());
    } catch (e) {
        const errorMessage: string = (e as Error).message;
        yield put(uploadMLTestDocumentFailed(errorMessage));
        toast.error('There was a problem uploading your document. Please try again.');
    }
}

function* uploadWatcher() {
    yield takeLatest(AdminAIPlaygroundActionTypes.UPLOAD_ML_TEST_DOCUMENT_STARTED, attemptUploadMLTestDocuments);
}

export function* attemptOpenMLTestDocument({ payload }: ReturnType<typeof openMLTestDocumentStarted>) {
    try {
        const { location, documentId, mimeType } = payload;
        const mlTestDocumentBlob: Blob = yield call(openMLTestDocument, { location });
        const mlTestDocument: MLTestDocument = yield call(getMLTestDocumentById, { mlTestDocumentId: documentId });
        const mlTestDocumentUrl: string = yield call(getDocumentUrl, mimeType, mlTestDocumentBlob);
        yield put(openMLTestDocumentSuccessful(mlTestDocumentUrl, mlTestDocument));
    } catch (e) {
        yield put(openMLTestDocumentFailed((e as Error).message, payload.location));
        toast.error('Unable to open document. Please try again.');
    }
}

function* openMLTestDocumentWatcher() {
    yield takeLeading(AdminAIPlaygroundActionTypes.OPEN_ML_TEST_DOCUMENT_STARTED, attemptOpenMLTestDocument);
}

export function* askAIPlaygroundQuestions() {
    try {
        const mlTestDocument: MLTestDocument = yield select(getMLTestDocument);
        const currentQueries: PlaygroundQuestion[] = yield select(getCurrentSelectedQueries);

        const { mlTestDocumentId, scheduleStartPage, scheduleEndPage } = mlTestDocument;
        if (!isNull(scheduleStartPage) && !isNull(scheduleEndPage)) {
            const questions: PlaygroundQuestion[] = currentQueries.map(({ question, queryKey, guidanceNotes }) => ({ question, queryKey, guidanceNotes, correct: false }));
            const request: AIPlaygroundRequest = { mlTestDocumentId, scheduleStartPage, scheduleEndPage, questions };

            const response: { data: MLData, mlTestDocumentExtractId: number } = yield call(askPlaygroundQuestions, request);
            yield put(askAIPlaygroundQuestionsSuccessful(response));
        }
    } catch (e) {
        yield put(askAIPlaygroundQuestionsFailed((e as Error).message));
    }
}

function* askAIPlaygroundQuestionsWatcher() {
    yield takeLatest(AdminAIPlaygroundActionTypes.ASK_AI_PLAYGROUND_QUESTIONS_STARTED, askAIPlaygroundQuestions);
}

export function* checkIsFilteringSelectedQueries() {
    yield put(setIsFilteringSelectedQueries(true));
    yield delay(500);
    yield put(setIsFilteringSelectedQueries(false));
}

function* checkIsFilteringSelectedQueriesWatcher() {
    yield takeLatest(AdminAIPlaygroundActionTypes.UPDATE_AI_PLAYGROUND_SEARCH, checkIsFilteringSelectedQueries);
}

export function* updateCorrectedPlaygroundQueries() {
    const correctQueriesIndexes: number[] = yield select(getCurrentCorrectQueries);
    const selectedQueries: PlaygroundQuestion[] = yield select(getCurrentSelectedQueries);
    const mlTestDocumentExtractId: number = yield select(getMlTestDocumentExtractId);
    const correct = selectedQueries.map((query, index) => set('correct', correctQueriesIndexes.includes(index), query));
    yield call(confirmCorrectPlaygroundQuestions, { mlTestDocumentExtractId, correct });
    yield put(updateCorrectPlaygroundQueries([]));
}

function* updateCorrectedPlaygroundQueriesWatcher() {
    yield takeLeading(AdminAIPlaygroundActionTypes.RESET_SELECTED_QUERIES_RESULTS, updateCorrectedPlaygroundQueries);
}

export function* checkMLAnswerHighlights() {
    try {
        const schedulePageText: PdfPageText[] = yield select(getPdfText);
        const mlData: MLData = yield select(getAIResults);
        const queryKeys = Object.keys(mlData).map(key => key);

        for (const [index, queryKey] of Array.from(queryKeys.entries())) {
            const { page, snippet } = mlData[queryKey];
            const pageText = schedulePageText.find(({ pageNumber }) => pageNumber === page);
            if (pageText) {
                const { uniqueIndexes, isFuzzyMatchHighlight } = getHighlightIndexesForPageText(snippet!, pageText);
                yield put(setPlaygroundAIAnswerHighlightIndexes(queryKey, uniqueIndexes, isFuzzyMatchHighlight));
            }
            if (index === (queryKeys.length - 1)) {
                yield put(setIsCheckingHighlightIndexes(false));
            }
        }
    } catch (e) {
        toast.error('We encountered a problem checking where the answers were found.');
    }
}

function* checkMLAnswerHighlightsWatcher() {
    yield debounce(1000, AdminAIPlaygroundActionTypes.ASK_AI_PLAYGROUND_QUESTIONS_SUCCESSFUL, checkMLAnswerHighlights);
}

export function* attemptFetchAllPlaygroundQuestionsAndAnswers({ payload }: ReturnType<typeof fetchAllPlaygroundQuestionsAndAnswersStarted>) {
    try {
        const { documentNameId } = payload;
        const result: AllPreviousPlaygroundQuestionsAndAnswers = yield call(fetchAllPlaygroundQuestionsAndAnswers, { documentNameId });
        yield put(fetchAllPlaygroundQuestionsAndAnswersSuccessful(result));
    } catch (e) {
        yield put(fetchAllPlaygroundQuestionsAndAnswersFailed((e as Error).message));
        toast.error('We encountered a problem fetching the questions and answers, please try again.');
    }
}

function* fetchAllPlaygroundQuestionsAndAnswersWatcher() {
    yield takeLatest(AdminAIPlaygroundActionTypes.FETCH_ALL_PREVIOUS_PLAYGROUND_QUESTIONS_AND_ANSWERS_STARTED, attemptFetchAllPlaygroundQuestionsAndAnswers);
}

export function* adminAIPlaygroundSaga() {
    yield all([
        fork(attemptFetchMLTestDocumentsWatcher),
        fork(mlTestDocumentsPaginationNextWatcher),
        fork(mlTestDocumentsPaginationPreviousWatcher),
        fork(mlTestDocumentsTableConfigUpdatedWatcher),
        fork(uploadWatcher),
        fork(openMLTestDocumentWatcher),
        fork(askAIPlaygroundQuestionsWatcher),
        fork(checkIsFilteringSelectedQueriesWatcher),
        fork(checkMLAnswerHighlightsWatcher),
        fork(updateCorrectedPlaygroundQueriesWatcher),
        fork(updateCorrectedPlaygroundQueriesWatcher),
        fork(fetchAllPlaygroundQuestionsAndAnswersWatcher)
    ]);
}
